import { http } from "@/lib/http";

export interface InterviewQuestion {
  id: string;
  prompt: string;
  hint: string;
  /** Profile dimensions this question fills; used to skip what Xavior already knows. */
  dimensions?: string[];
}

export interface InterviewQuestionsResponse {
  questions: InterviewQuestion[];
  filledDimensions: string[];
}

export interface InterviewAnswerResult {
  noted: string[];
  dimensionsUpdated: string[];
  contactsCreated: string[];
}

export async function getInterviewQuestions(
  authToken: string | null,
  workspaceId: string,
): Promise<InterviewQuestionsResponse> {
  const response = await http.get<InterviewQuestionsResponse>(
    `/workspaces/${encodeURIComponent(workspaceId)}/profile-interview`,
    { authToken },
  );
  return response.data;
}

export interface PeopleBucket {
  name: string;
  basis: "defined" | "proposed";
  isPeople: boolean;
  members: string[];
}
export interface PeopleMap {
  buckets: PeopleBucket[];
  totalPeople: number;
  allTags?: string[];
}

export async function manageTag(
  authToken: string | null,
  workspaceId: string,
  body: { action: "create" | "rename" | "delete"; tag?: string; from?: string; to?: string },
): Promise<{ ok: boolean }> {
  const response = await http.post<{ ok: boolean }>(
    `/workspaces/${encodeURIComponent(workspaceId)}/profile-interview/tags`,
    body,
    { authToken },
  );
  return response.data;
}

export interface SavePersonInput {
  id?: string;
  name?: string;
  tags?: string[];
  note?: string;
  birthday?: string;
  remove?: boolean;
}

export async function savePerson(
  authToken: string | null,
  workspaceId: string,
  input: SavePersonInput,
): Promise<{ ok: boolean; id?: string }> {
  const response = await http.post<{ ok: boolean; id?: string }>(
    `/workspaces/${encodeURIComponent(workspaceId)}/profile-interview/person`,
    input,
    { authToken },
  );
  return response.data;
}

export interface WorkBucket {
  name: string;
  members: string[];
}
export interface WorkMap {
  buckets: WorkBucket[];
  total: number;
  allTags?: string[];
}
export async function getWorkMap(
  authToken: string | null,
  workspaceId: string,
  force = false,
): Promise<WorkMap> {
  const response = await http.get<WorkMap>(
    `/workspaces/${encodeURIComponent(workspaceId)}/profile-interview/work-map${force ? "?force=1" : ""}`,
    { authToken },
  );
  return response.data;
}

export async function manageWorkTag(
  authToken: string | null,
  workspaceId: string,
  body: { action: "create" | "rename" | "delete"; tag?: string; from?: string; to?: string },
): Promise<{ ok: boolean }> {
  const response = await http.post<{ ok: boolean }>(
    `/workspaces/${encodeURIComponent(workspaceId)}/profile-interview/work-tags`,
    body,
    { authToken },
  );
  return response.data;
}

export async function saveTask(
  authToken: string | null,
  workspaceId: string,
  input: { id?: string; title?: string; tags?: string[]; note?: string; done?: boolean; remove?: boolean },
): Promise<{ ok: boolean; id?: string }> {
  const response = await http.post<{ ok: boolean; id?: string }>(
    `/workspaces/${encodeURIComponent(workspaceId)}/profile-interview/task`,
    input,
    { authToken },
  );
  return response.data;
}

/** Work discussion → establishes areas of work (project tags). Same shape as
 *  submitInterviewAnswer so it can drop into InterviewPanel's onAnswer. */
export async function submitWorkAnswer(
  authToken: string | null,
  workspaceId: string,
  _question: string,
  answer: string,
): Promise<InterviewAnswerResult> {
  const response = await http.post<InterviewAnswerResult>(
    `/workspaces/${encodeURIComponent(workspaceId)}/profile-interview/work-answer`,
    { answer },
    { authToken },
  );
  return response.data;
}

// ── Curated Work tab — areas + items, decoupled from the Task module. ──────────
export interface WorkItemC {
  id: string;
  text: string;
  note?: string;
  done?: boolean;
}
export interface WorkAreaC {
  id: string;
  name: string;
  items: WorkItemC[];
}
export interface WorkDataC {
  areas: WorkAreaC[];
}
export async function getWork(
  authToken: string | null,
  workspaceId: string,
): Promise<WorkDataC> {
  const response = await http.get<WorkDataC>(
    `/workspaces/${encodeURIComponent(workspaceId)}/profile-interview/work`,
    { authToken },
  );
  return response.data;
}
export async function saveWorkArea(
  authToken: string | null,
  workspaceId: string,
  body: { op: "add" | "rename" | "remove"; id?: string; name?: string },
): Promise<WorkDataC> {
  const response = await http.post<WorkDataC>(
    `/workspaces/${encodeURIComponent(workspaceId)}/profile-interview/work-area`,
    body,
    { authToken },
  );
  return response.data;
}
export async function saveWorkItem(
  authToken: string | null,
  workspaceId: string,
  body: {
    areaId: string;
    op: "add" | "edit" | "remove";
    item: { id?: string; text?: string; note?: string; done?: boolean };
  },
): Promise<WorkDataC> {
  const response = await http.post<WorkDataC>(
    `/workspaces/${encodeURIComponent(workspaceId)}/profile-interview/work-item`,
    body,
    { authToken },
  );
  return response.data;
}

export interface Trip {
  id: string;
  destination: string;
  start: string; // ISO YYYY-MM-DD
  end: string; // ISO YYYY-MM-DD
  stay: string;
  note: string;
}
export interface Favorite {
  id: string;
  place: string;
  name: string;
  note: string;
  photoUrl?: string;
}
export async function getTrips(
  authToken: string | null,
  workspaceId: string,
): Promise<{ trips: Trip[]; favorites: Favorite[] }> {
  const response = await http.get<{ trips: Trip[]; favorites: Favorite[] }>(
    `/workspaces/${encodeURIComponent(workspaceId)}/profile-interview/travel`,
    { authToken },
  );
  return response.data;
}
export async function saveTrip(
  authToken: string | null,
  workspaceId: string,
  input: { id?: string; destination?: string; start?: string; end?: string; stay?: string; note?: string; remove?: boolean },
): Promise<{ ok: boolean }> {
  const response = await http.post<{ ok: boolean }>(
    `/workspaces/${encodeURIComponent(workspaceId)}/profile-interview/trip`,
    input,
    { authToken },
  );
  return response.data;
}
export async function saveFavorite(
  authToken: string | null,
  workspaceId: string,
  input: { id?: string; place?: string; name?: string; note?: string; remove?: boolean },
): Promise<{ ok: boolean }> {
  const response = await http.post<{ ok: boolean }>(
    `/workspaces/${encodeURIComponent(workspaceId)}/profile-interview/favorite`,
    input,
    { authToken },
  );
  return response.data;
}
export async function submitTravelAnswer(
  authToken: string | null,
  workspaceId: string,
  _question: string,
  answer: string,
): Promise<InterviewAnswerResult> {
  const response = await http.post<InterviewAnswerResult>(
    `/workspaces/${encodeURIComponent(workspaceId)}/profile-interview/travel-answer`,
    { answer },
    { authToken },
  );
  return response.data;
}

export type HealthCategory =
  | "medications"
  | "conditions"
  | "history"
  | "exercise"
  | "diet";
export interface HealthItem {
  id: string;
  category: HealthCategory;
  text: string;
}
export interface HealthData {
  items: HealthItem[];
  recommendations: string;
}
export async function getHealth(
  authToken: string | null,
  workspaceId: string,
): Promise<HealthData> {
  const response = await http.get<HealthData>(
    `/workspaces/${encodeURIComponent(workspaceId)}/profile-interview/health`,
    { authToken },
  );
  return response.data;
}
export async function removeHealthItem(
  authToken: string | null,
  workspaceId: string,
  id: string,
): Promise<HealthData> {
  const response = await http.post<HealthData>(
    `/workspaces/${encodeURIComponent(workspaceId)}/profile-interview/health-item`,
    { op: "remove", item: { id } },
    { authToken },
  );
  return response.data;
}
export async function recategorizeHealth(
  authToken: string | null,
  workspaceId: string,
): Promise<HealthData> {
  const response = await http.post<HealthData>(
    `/workspaces/${encodeURIComponent(workspaceId)}/profile-interview/health-recategorize`,
    {},
    { authToken },
  );
  return response.data;
}

/** Health discussion → captures health facts. Same shape as submitInterviewAnswer
 *  so it drops into InterviewPanel's onAnswer. */
export async function submitHealthAnswer(
  authToken: string | null,
  workspaceId: string,
  _question: string,
  answer: string,
): Promise<InterviewAnswerResult> {
  const response = await http.post<InterviewAnswerResult>(
    `/workspaces/${encodeURIComponent(workspaceId)}/profile-interview/health-answer`,
    { answer },
    { authToken },
  );
  return response.data;
}

export interface HistoryEntryBase {
  id: string;
  source: "linkedin" | "manual";
}
export interface WorkEntry extends HistoryEntryBase {
  company: string;
  /** Optional — the UI accepts bare "Company Year" entries (no job title). */
  title?: string;
  startYear: number;
  endYear?: number;
  location?: string;
  /** Short description of what the company/role does — the ladder's third line. */
  summary?: string;
}
export interface LocationEntry extends HistoryEntryBase {
  place: string;
  startYear: number;
  endYear?: number;
}
export interface LifeEvent extends HistoryEntryBase {
  label: string;
  year: number;
}
export interface SkillEntry extends HistoryEntryBase {
  label: string;
}
export interface HistoryData {
  work: WorkEntry[];
  locations: LocationEntry[];
  lifeEvents: LifeEvent[];
  skills: SkillEntry[];
}
export type HistoryTrack = "work" | "locations" | "lifeEvents" | "skills";

export async function getHistory(
  authToken: string | null,
  workspaceId: string,
): Promise<HistoryData> {
  const response = await http.get<HistoryData>(
    `/workspaces/${encodeURIComponent(workspaceId)}/profile-interview/history`,
    { authToken },
  );
  return response.data;
}

export async function saveHistoryItem(
  authToken: string | null,
  workspaceId: string,
  input: {
    track: HistoryTrack;
    op: "add" | "edit" | "remove";
    item: Record<string, unknown>;
  },
): Promise<HistoryData> {
  const response = await http.post<HistoryData>(
    `/workspaces/${encodeURIComponent(workspaceId)}/profile-interview/history-item`,
    input,
    { authToken },
  );
  return response.data;
}

export async function saveHistorySkills(
  authToken: string | null,
  workspaceId: string,
  skills: string[],
): Promise<HistoryData> {
  const response = await http.post<HistoryData>(
    `/workspaces/${encodeURIComponent(workspaceId)}/profile-interview/history-skills`,
    { skills },
    { authToken },
  );
  return response.data;
}

// ── Profile summary (Overview) ───────────────────────────────────────────────
export interface ProfileSummary {
  profile: string;
  generatedAt: string | null;
}
export async function getProfileSummary(
  authToken: string | null,
  workspaceId: string,
): Promise<ProfileSummary> {
  const response = await http.get<ProfileSummary>(
    `/workspaces/${encodeURIComponent(workspaceId)}/profile-interview/profile-summary`,
    { authToken },
  );
  return response.data;
}
export async function refreshProfileSummary(
  authToken: string | null,
  workspaceId: string,
): Promise<ProfileSummary> {
  const response = await http.post<ProfileSummary>(
    `/workspaces/${encodeURIComponent(workspaceId)}/profile-interview/profile-summary`,
    {},
    { authToken },
  );
  return response.data;
}

// ── Watchlist ───────────────────────────────────────────────────────────────
export type WatchCategory = "price" | "event" | "topic";
export interface WatchItem {
  id: string;
  source: "manual" | "extracted";
  label: string;
  category: WatchCategory;
  note?: string;
}
export interface WatchlistData {
  items: WatchItem[];
}

export async function getWatchlist(
  authToken: string | null,
  workspaceId: string,
): Promise<WatchlistData> {
  const response = await http.get<WatchlistData>(
    `/workspaces/${encodeURIComponent(workspaceId)}/profile-interview/watchlist`,
    { authToken },
  );
  return response.data;
}

export async function saveWatchlistItem(
  authToken: string | null,
  workspaceId: string,
  input: { op: "add" | "edit" | "remove"; item: Record<string, unknown> },
): Promise<WatchlistData> {
  const response = await http.post<WatchlistData>(
    `/workspaces/${encodeURIComponent(workspaceId)}/profile-interview/watchlist-item`,
    input,
    { authToken },
  );
  return response.data;
}

export async function submitWatchlistAnswer(
  authToken: string | null,
  workspaceId: string,
  _question: string,
  answer: string,
): Promise<InterviewAnswerResult> {
  const response = await http.post<InterviewAnswerResult>(
    `/workspaces/${encodeURIComponent(workspaceId)}/profile-interview/watchlist-answer`,
    { answer },
    { authToken },
  );
  return response.data;
}

// "Talk to Xavior about your history" — free text → extracted + merged into the
// History blob. Signature matches InterviewPanel's onAnswer (question, answer).
export async function submitHistoryAnswer(
  authToken: string | null,
  workspaceId: string,
  _question: string,
  answer: string,
): Promise<InterviewAnswerResult> {
  const response = await http.post<InterviewAnswerResult>(
    `/workspaces/${encodeURIComponent(workspaceId)}/profile-interview/history-answer`,
    { answer },
    { authToken },
  );
  return response.data;
}

export async function getPeopleMap(
  authToken: string | null,
  workspaceId: string,
  force = false,
): Promise<PeopleMap> {
  const response = await http.get<PeopleMap>(
    `/workspaces/${encodeURIComponent(workspaceId)}/profile-interview/people-map${force ? "?force=1" : ""}`,
    { authToken },
  );
  return response.data;
}

export async function submitInterviewAnswer(
  authToken: string | null,
  workspaceId: string,
  question: string,
  answer: string,
): Promise<InterviewAnswerResult> {
  const response = await http.post<InterviewAnswerResult>(
    `/workspaces/${encodeURIComponent(workspaceId)}/profile-interview/answer`,
    { question, answer },
    { authToken },
  );
  return response.data;
}
